import { useAuth } from '@commander/auth';
import { CommanderGoButton, CommanderGoPanel, useCommanderGo } from '@commander/commander-go';
import { useFeatureFlags } from '@commander/feature-flags';
import { NotificationBell } from '@commander/notifications';
import { UniversalSearchBar } from '@commander/search';
import { useThemeToggle } from '@commander/theme-changer';
import { AppSwitcherMenu, Button, TrialButton } from '@commander/ui';
import { openTrialUpgrade, useTrialStatus, useUserDetails } from '@commander/user-details';
import { RiMoonLine as MoonIcon, RiSunLine as SunIcon } from '@remixicon/react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CommanderLogo } from '../assets/icons/AuthIcon';
import { ProfileMenu } from './ProfileMenu';

export function GlobalHeader({ offsetTop = false }: { offsetTop?: boolean }) {
  const { user } = useAuth();
  const { flags } = useFeatureFlags();
  const { isDark, toggleTheme, isLoading } = useThemeToggle();
  const commanderGo = useCommanderGo();
  const { isTrial, trialEndTs } = useTrialStatus();
  const { refetch: refetchUserDetails } = useUserDetails();
  // /users/details is cached (staleTime 5min, no refetch on mount), so an admin
  // flipping account_type / the trial window wouldn't reach a live tenant session
  // until the cache expires. Refetch once when a trial is in view so a conversion
  // (trial → full) or a window edit is reflected on the next app load. Scoped to
  // trial sessions — full accounts keep the cached fast path.
  const didRefetchTrial = useRef(false);
  useEffect(() => {
    if (isTrial && !didRefetchTrial.current) {
      didRefetchTrial.current = true;
      refetchUserDetails();
    }
  }, [isTrial, refetchUserDetails]);
  const appAccess = user?.company_employee?.app_access ?? [];
  const hasCommanderGoAccess = appAccess.includes('crm') || appAccess.includes('prospecting');
  const navigate = useNavigate();
  // The Global Search page has its own search box.
  const onGlobalSearch = useLocation().pathname.startsWith('/global-search');

  const handleNotificationsClick = () => {
    navigate('/notifications');
  };
  return (
    <header
      className={`fixed left-0 right-0 z-50 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 ${
        offsetTop ? 'top-9' : 'top-0'
      }`}
    >
      <div className="flex items-center gap-4">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 flex items-center justify-center text-current">
            <CommanderLogo />
          </div>
          <span className="hidden font-semibold text-lg md:inline-block">Commander</span>
        </div>

        {/* App Switcher */}
        <div className="border-l pl-4 ml-2">
          <AppSwitcherMenu appName="Home" appColor="#3b82f6" user={user} />
        </div>

        {/* Trial status / upgrade (left side — the top-right corner is the Chatwoot bubble) */}
        <TrialButton isTrial={isTrial} trialEndTs={trialEndTs} onUpgrade={openTrialUpgrade} />
      </div>

      {(flags.global_search || flags.action_dash) && !onGlobalSearch ? (
        <UniversalSearchBar placeholder="Look up a person or company" showKbdShortcut />
      ) : (
        <div className="hidden flex-1 md:block" />
      )}
      <div className="flex-1 md:hidden" />

      {/* Right side items */}
      <div className="flex items-center gap-2">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          disabled={isLoading}
          className="h-9 w-9"
        >
          {isDark ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
        </Button>

        {/* Commander Go — CRM/Prospecting access required, and not on a trial plan */}
        {hasCommanderGoAccess && !isTrial && (
          <>
            <CommanderGoButton onClick={commanderGo.toggle} />
            <CommanderGoPanel
              isOpen={commanderGo.isOpen}
              onClose={commanderGo.close}
              messages={commanderGo.messages}
              isLoading={commanderGo.isLoading}
              status={commanderGo.status}
              error={commanderGo.error}
              onSend={commanderGo.sendMessage}
              onNewChat={commanderGo.newChat}
              onRetry={commanderGo.regenerate}
              threads={commanderGo.threads}
              showThreadList={commanderGo.showThreadList}
              onToggleThreadList={commanderGo.toggleThreadList}
              onSelectThread={commanderGo.loadThread}
              onDeleteThread={commanderGo.deleteThread}
              onRenameThread={commanderGo.renameThread}
              onTogglePinThread={commanderGo.togglePinThread}
              onClearAllThreads={commanderGo.clearAllThreads}
              activeThreadId={commanderGo.threadId}
              hasMoreThreads={commanderGo.hasMoreThreads}
              onLoadMoreThreads={commanderGo.loadMoreThreads}
              onLoadOlder={commanderGo.loadOlderMessages}
              hasPreviousMessages={commanderGo.hasPreviousMessages}
              isLoadingOlder={commanderGo.isLoadingOlder}
            />
          </>
        )}

        {/* Notifications */}
        <NotificationBell onClick={handleNotificationsClick} size="sm" />

        {/* User Menu */}
        <ProfileMenu
          go={(path) => {
            // /account is a full page load, as it always was from this menu.
            if (path === '/account') window.location.href = path;
            else navigate(path);
          }}
        />
      </div>
    </header>
  );
}
